import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { churchInfo as fallback } from '../mock';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Camel <-> snake mapping so React components read a consistent camelCase shape,
// while the API stays canonical snake_case.
const toCamel = (api) => ({
  name: api.name ?? fallback.name,
  address: api.address ?? fallback.address,
  shortAddress: api.short_address ?? fallback.shortAddress,
  email: api.email ?? fallback.email,
  phone: api.phone ?? fallback.phone,
  mapEmbed: api.map_embed ?? fallback.mapEmbed,
  mapLocation: api.map_location ?? fallback.mapLocation,
  youtubeChannelUrl: api.youtube_channel_url ?? fallback.youtubeChannelUrl,
  youtubeEmbedUrl: api.youtube_embed_url ?? fallback.youtubeEmbedUrl,
  facebookUrl: api.facebook_url ?? '',
  instagramUrl: api.instagram_url ?? '',
  nehemiahYoutubeEmbedUrl: api.nehemiah_youtube_embed_url ?? '',
  serviceTimes: Array.isArray(api.service_times) && api.service_times.length
    ? api.service_times
    : fallback.serviceTimes,
});

const SiteSettingsContext = createContext({
  siteInfo: toCamel({}),  // hydrated with fallback from mock so pages render instantly
  refresh: async () => {},
});

export const SiteSettingsProvider = ({ children }) => {
  const [siteInfo, setSiteInfo] = useState(() => toCamel({}));

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/site-settings`);
      if (data && typeof data === 'object') {
        setSiteInfo(toCamel(data));
      }
    } catch (e) {
      // Keep fallback silently — network hiccups shouldn't break the site.
      console.warn('[site-settings] refresh failed; using fallback', e);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ siteInfo, refresh }), [siteInfo, refresh]);

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteInfo = () => useContext(SiteSettingsContext).siteInfo;
export const useSiteSettings = () => useContext(SiteSettingsContext);
